import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Bot, User, ChevronDown } from "lucide-react";
import { db } from "@/lib/firebase";
import { doc, getDoc, collection, addDoc } from "firebase/firestore";
import { incrementCounter } from "@/lib/stats";

interface Message {
  role: "user" | "assistant";
  content: string;
}

function getSessionId() {
  const key = "eliada_chat_session";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem(key, id);
  }
  return id;
}

export function ChatWidget() {
  const [minimized, setMinimized] = useState(false);
  const sessionId = getSessionId();
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Hi! I'm the Eliada Sports Agency assistant. How can I help you today? You can ask me about our services, how to apply, or anything else about the agency.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [config, setConfig] = useState<any>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const docRef = doc(db, "configs", "generalSettings");
    getDoc(docRef)
      .then(snap => {
        if (snap.exists()) {
          const site = snap.data();
          const enabled = site.chatbot_enabled === "true" && !!site.chatbot_api_key?.trim();
          setAvailable(enabled);
          if (enabled) {
            setConfig(site);
          }
        } else {
          setAvailable(false);
        }
      })
      .catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    if (!minimized) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [minimized]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || !config) return;

    const userMsg: Message = { role: "user", content: text };
    const history = messages.slice(-10);
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const apiKey = config.chatbot_api_key;
      const provider = config.chatbot_provider || "openai";
      const model = config.chatbot_model || (provider === "anthropic" ? "claude-3-5-haiku-20241022" : "gpt-4o-mini");
      const systemPrompt = config.chatbot_system_prompt || "You are a helpful assistant for Eliada Sports Agency.";

      let reply = "";

      if (provider === "anthropic") {
        const anthropicHistory = history.map(m => ({ role: m.role, content: m.content }));
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model,
            max_tokens: 500,
            system: systemPrompt,
            messages: [...anthropicHistory, { role: "user", content: text }],
          }),
        });
        if (!res.ok) throw new Error("API call failed");
        const data = await res.json();
        reply = data?.content?.[0]?.text ?? "I'm sorry, I couldn't generate a response.";
      } else {
        const baseUrls: Record<string, string> = {
          openai: "https://api.openai.com/v1",
          groq: "https://api.groq.com/openai/v1",
          openrouter: "https://openrouter.ai/api/v1",
        };
        const customBase = config.chatbot_base_url?.trim();
        const baseURL = customBase || baseUrls[provider] || baseUrls["openai"];

        const messagesForApi = [
          { role: "system", content: systemPrompt },
          ...history.map(m => ({ role: m.role, content: m.content })),
          { role: "user", content: text }
        ];

        const res = await fetch(`${baseURL}/chat/completions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model,
            messages: messagesForApi,
            max_tokens: 500,
            temperature: 0.7,
          }),
        });
        if (!res.ok) throw new Error("API call failed");
        const data = await res.json();
        reply = data?.choices?.[0]?.message?.content ?? "I'm sorry, I couldn't generate a response.";
      }

      // Log conversation to Firestore
      try {
        await addDoc(collection(db, "chat_conversations"), {
          sessionId,
          pageUrl: window.location.pathname,
          message: text,
          reply,
          createdAt: new Date().toISOString(),
        });
        try { await incrementCounter("chat_conversations"); } catch (e) {}
      } catch (err) {
        console.warn("Firestore chat logging skipped:", err);
      }

      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't get a response. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!available) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 w-[340px] max-w-[calc(100vw-2rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300"
      style={{ height: minimized ? "auto" : "480px" }}>

      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground shrink-0 cursor-pointer select-none"
        onClick={() => setMinimized(v => !v)}
      >
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <Bot size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight">Eliada Assistant</p>
          <p className="text-[11px] text-white/70 leading-tight">Ask me anything about the agency</p>
        </div>
        <ChevronDown
          size={18}
          className={`text-white/80 transition-transform duration-300 ${minimized ? "rotate-180" : "rotate-0"}`}
        />
      </div>

      {/* Body — hidden when minimized */}
      {!minimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50/50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                {msg.role === "assistant" ? (
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot size={14} className="text-primary" />
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                    <User size={14} className="text-gray-600" />
                  </div>
                )}
                <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                    : "bg-white text-gray-800 shadow-sm border border-gray-100 rounded-tl-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot size={14} className="text-primary" />
                </div>
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-100 bg-white shrink-0">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10 transition-all">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Type a message…"
                disabled={loading}
                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none disabled:opacity-60"
              />
              <button
                onClick={send}
                disabled={!input.trim() || loading}
                className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 disabled:opacity-40 transition-all shrink-0"
                aria-label="Send message"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-1.5">Powered by Eliada Sports Agency</p>
          </div>
        </>
      )}
    </div>
  );
}
